import React, { useEffect, useMemo, useRef, useState } from "react";
import { createOrGetConversation, fetchMessages, sendMessage } from "../api/chatApi";
import { getSocket } from "./socket";
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';
import { Button, Input, Card } from "../components/ui";

export default function SupportChat() {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [status, setStatus] = useState("init");

  const [isOpen, setIsOpen] = useState(false);

  const bottomRef = useRef(null);
  const socket = useMemo(() => getSocket(), []);

  function scrollToBottom() {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      try {
        setStatus("init");
        const conv = await createOrGetConversation();
        setConversation(conv);

        const msgs = await fetchMessages(conv._id);
        setMessages(msgs);

        socket.emit("conversation:join", { conversationId: conv._id });

        setStatus("ready");
        scrollToBottom();
      } catch (e) {
        console.error(e);
        setStatus("error");
      }
    })();
  }, [socket, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    function onNewMessage(payload) {
      const msg = payload?.message;
      if (!msg) return;

      setMessages((prev) => {
        if (prev.some((m) => m._id === msg._id)) return prev;
        return [...prev, msg];
      });
      scrollToBottom();
    }

    socket.on("message:new", onNewMessage);
    return () => socket.off("message:new", onNewMessage);
  }, [socket, isOpen]);

  async function onSend(e) {
    e.preventDefault();
    if (!conversation) return;

    const body = text.trim();
    if (!body) return;

    setText("");

    try {
      const msg = await sendMessage(conversation._id, body);
      setMessages((prev) => {
        if (prev.some((m) => m._id === msg._id)) return prev;
        return [...prev, msg];
      });
      scrollToBottom();
    } catch (e) {
      console.error(e);
      alert("Erreur envoi message");
    }
  }

  return (
    <div className="fixed bottom-5 right-5 z-[9999] flex flex-col items-end">
      {isOpen && (
        <Card padding={false} className="w-[350px] h-[500px] flex flex-col mb-4 overflow-hidden shadow-lg">
          <div className="flex items-center justify-between bg-blue-500 text-white p-4">
            <span className="font-bold">Support technique</span>
            <button
              onClick={() => setIsOpen(false)}
              className="bg-transparent border-none text-white cursor-pointer text-lg leading-none"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
            {status === "error" ? (
              <div className="text-red-500">Erreur de connexion au support.</div>
            ) : (
              messages.map((m) => (
                <div
                  key={m._id}
                  className={`mb-2.5 flex flex-col ${m.senderType === 'user' ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`px-3 py-2 rounded-xl max-w-[80%] text-sm ${
                      m.senderType === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-900'
                    }`}
                  >
                    {m.body}
                  </div>
                  <div className="text-[10px] opacity-50 mt-1">
                    {new Date(m.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={onSend} className="flex gap-2 p-3 border-t border-gray-200 bg-white">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Écrire un message..."
              className="flex-1 rounded-[20px]"
            />
            <Button type="submit" variant="ghost" disabled={!text.trim()}>
              Envoyer
            </Button>
          </form>
        </Card>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-[60px] h-[60px] rounded-full bg-blue-500 text-white border-none shadow-lg shadow-blue-500/40 cursor-pointer flex items-center justify-center text-2xl transition-transform duration-200 hover:scale-105 active:scale-95"
        title="Ouvrir le support"
      >
        {isOpen ? '✕' : '💬'}
      </button>
    </div>
  );
}
